import { useNavigate } from 'react-router-dom'
import SharedFilters from '../../../components/SharedFilters'
import AppActionMenu from '../../../components/AppActionMenu'
import AppPagination from '../../../components/AppPagination'
import { useExportJob } from '../../../hooks/useExportJob'
import { useAdmissionFormSettingListScreen } from './hooks/useAdmissionFormSettingListScreen'

export default function AdmissionFormSettingListScreen() {
  const navigate = useNavigate()
  const { state, actions } = useAdmissionFormSettingListScreen()
  const { triggerExport, isExporting } = useExportJob({ module: 'admission_form_setting' })

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-8">
      <section className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div className="max-w-4xl">
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Admission</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Admission Form Setting</span>
          </div>
          <h1 className="mt-2 text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            Admission Form Settings
          </h1>
          <p className="mt-2 text-base leading-relaxed text-on-surface-variant">
            Configure the admission form layout, required fields, and field labels for each student and guardian section.
          </p>
        </div>
      </section>

      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        companyFilter={state.companyFilter}
        branchFilter={state.branchFilter}
        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        setCompanyFilter={actions.setCompanyFilter}
        setBranchFilter={actions.setBranchFilter}
        onReset={actions.resetFilters}
        onSearch={() => actions.refresh()}
        searchFieldOptions={[
          { value: 'Name', label: 'Name' },
          { value: 'Company', label: 'Company' },
          { value: 'Branch', label: 'Branch' },
        ]}
        companyOptions={state.options.companyOptions.map((option) => ({ value: option, label: option }))}
        branchOptions={state.options.branchOptions.map((option) => ({ value: option, label: option }))}
        visibleAccordionFilters={['company', 'branch']}
        showStatus={false}
        showSearchButtons={true}
        showSearch={true}
        showAccordionToggle={true}
        tools={[
          { key: 'export', label: 'Export Settings', icon: 'download' },
          { key: 'print', label: 'Print List', icon: 'print' },
        ]}
        onToolSelect={(key) => {
          if (key === 'export') {
            triggerExport({
              searchField: state.searchField,
              searchText: state.searchText,
              company: state.companyFilter === 'All' ? '' : state.companyFilter,
              branch: state.branchFilter === 'All' ? '' : state.branchFilter,
            })
          }
          if (key === 'print') window.print()
        }}
      />

      {state.error && (
        <div className="rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">
          {state.error}
        </div>
      )}

      <section className="overflow-hidden rounded-[2rem] bg-surface-container-low">
        <div className="grid grid-cols-12 px-6 py-4 text-[11px] font-black uppercase tracking-[0.14em] text-outline/90">
          <div className="col-span-3">Form</div>
          <div className="col-span-3">Company</div>
          <div className="col-span-3">Branch</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>

        <div className="space-y-3 p-4">
          {state.isLoading ? (
            <div className="rounded-xl bg-surface-container-lowest px-6 py-10 text-center text-sm text-on-surface-variant">
              Loading form settings...
            </div>
          ) : state.rows.length === 0 ? (
            <div className="rounded-xl bg-surface-container-lowest px-6 py-10 text-center text-sm text-on-surface-variant">
              No form settings match the current filters.
            </div>
          ) : (
            state.rows.map((row) => (
              <article key={row.id} className="grid grid-cols-12 items-center rounded-xl bg-surface-container-lowest px-6 py-5 shadow-sm transition hover:-translate-y-0.5">
                <div className="col-span-3">
                  <p className="text-base font-bold text-on-surface">{row.name}</p>
                  <p className="text-xs text-on-surface-variant">Updated {row.updatedAt || 'Recently'}</p>
                </div>

                <div className="col-span-3 text-sm text-on-surface-variant">{row.company}</div>

                <div className="col-span-3 text-sm text-on-surface-variant">{row.branch}</div>

                <div className="col-span-3 flex items-center justify-end">
                  <AppActionMenu
                    triggerLabel=""
                    triggerIcon="more_vert"
                    showChevron={false}
                    triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                    items={[
                      { key: 'edit', label: 'Edit', icon: 'edit' },
                      { key: 'delete', label: 'Delete', icon: 'delete' },
                    ]}
                    onSelect={(key) => {
                      if (key === 'edit') {
                        navigate(`/admission/admission-form-setting/entry?id=${row.id}`)
                      } else if (key === 'delete') {
                        actions.handleDelete(row.id)
                      }
                    }}
                  />
                </div>
              </article>
            ))
          )}
        </div>
      </section>

      {/* Shared Pagination */}
      <div className="flex items-center justify-between gap-3 rounded-[1.5rem] bg-surface-container-low px-4 py-3">
        <div className="text-sm text-on-surface-variant">
          Showing {state.rows.length} of {state.meta.totalCount} settings
        </div>
        <AppPagination
          page={state.page}
          totalPages={state.meta.totalPages || 1}
          onPageChange={actions.setPage}
          onPrevious={() => actions.setPage(Math.max(1, state.page - 1))}
          onNext={() => actions.setPage(Math.min(state.meta.totalPages || 1, state.page + 1))}
          canPrevious={state.page > 1}
          canNext={state.page < (state.meta.totalPages || 1)}
        />
      </div>
    </div>
  )
}
